import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useCallback, useRef } from 'react';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { Message, MessageRole } from 'pages/ws/components/chatSidebar/WsChatSidebar';
import {
  COVER_IMAGE_DONE_MESSAGE,
  COVER_IMAGE_ERROR_MESSAGE,
  COVER_IMAGE_LIMIT_MESSAGE,
  isAiImageCountLimitReached,
  isFilled,
} from 'pages/ws/utils/wsContentAiChat';

type UseWsCoverImageGenerateParams = {
  getTitle: () => string | undefined;
  getDescription: () => string | undefined;
  setImageKey: (imageKey: string) => void;
};

/**
 * 表紙画像生成処理（EV-content-chat-11）。診断・クイズ・フォームで共通の処理を集約する。
 * 上限判定は付与時と同じロジックで押下時にも再実行する（凍結 config の closure が
 * 古い acctState を持つため、付与時の判定だけでは上限超過を防げない）。
 */
export function useWsCoverImageGenerate(
  params: UseWsCoverImageGenerateParams
): (setMessages: React.Dispatch<React.SetStateAction<Message[]>>) => Promise<void> {
  const [acctState, setAcctState] = useAtom(AcctState);
  const { postCompanyImageRegisterAi } = useApiState();
  const { getActivePlan, isAiImageCountNoLimit } = useAppState();

  // 凍結 config 対策: chatConfig の closure は開いた時点で固定されるため、
  // 最新の acctState を ref 経由で参照する（WsDiagnosisEditChatbot.tsx の同パターンを踏襲）。
  const acctStateRef = useRef(acctState);
  // eslint-disable-next-line react-hooks/refs -- レンダーごとにrefを最新値に同期するパターン（イベントハンドラ用）
  acctStateRef.current = acctState;

  // 引数（getTitle / getDescription / setImageKey）はレンダーごとに新しい関数インスタンスに
  // なるため、acctStateRef と同じく ref に同期して useCallback の依存配列から外す。
  const paramsRef = useRef(params);
  // eslint-disable-next-line react-hooks/refs -- レンダーごとにrefを最新値に同期するパターン（イベントハンドラ用）
  paramsRef.current = params;

  return useCallback(
    async (setMessages: React.Dispatch<React.SetStateAction<Message[]>>) => {
      const title = paramsRef.current.getTitle();
      const description = paramsRef.current.getDescription();

      const imageCountLimitReached = isAiImageCountLimitReached({
        aiImageUsageCount: acctStateRef.current.company.aiImageUsageCount,
        aiImageCountLimit: getActivePlan()?.aiImageCountLimit,
        noLimit: isAiImageCountNoLimit(),
      });
      if (imageCountLimitReached) {
        setMessages(prev => [
          ...prev,
          { role: 'ai' as MessageRole, text: COVER_IMAGE_LIMIT_MESSAGE },
        ]);
        return;
      }

      if (!isFilled(title)) {
        setMessages(prev => [
          ...prev,
          { role: 'ai' as MessageRole, text: COVER_IMAGE_ERROR_MESSAGE },
        ]);
        return;
      }

      try {
        const response = await postCompanyImageRegisterAi({
          companyId: acctStateRef.current.company.id,
          prompt: null,
          contentTitle: title.trim().slice(0, 100),
          contentDescription: description ?? null,
        });

        paramsRef.current.setImageKey(response.imageKey);
        setMessages(prev => [
          ...prev,
          { role: 'ai' as MessageRole, text: COVER_IMAGE_DONE_MESSAGE },
        ]);
        setAcctState(prev => ({
          ...prev,
          company: {
            ...prev.company,
            aiImageUsageCount: response.aiImageUsageCount,
          },
        }));
      } catch (error) {
        console.error('表紙画像生成エラー:', error);
        setMessages(prev => [
          ...prev,
          { role: 'ai' as MessageRole, text: COVER_IMAGE_ERROR_MESSAGE },
        ]);
      }
    },
    [getActivePlan, isAiImageCountNoLimit, postCompanyImageRegisterAi, setAcctState]
  );
}
